import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { CommonError } from 'src/api/base'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import PortfolioSemesterSelect from 'src/views/includes/PortfolioSemesterSelect'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

export default function AdministratorPortfolioLimitsPage() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('portfolio:limits')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const activeSemester = useActiveSemester()
  const [searchParams] = useSearchParams({
    page: '1',
    perPage: '10',
  })

  const page = searchParams.get('page') ?? '1'
  const perPage = searchParams.get('perPage') ?? '10'
  const semId = searchParams.get('semId') ?? activeSemester?.id ?? ''

  // Fetch limits
  const { data: limits, error } = useApi({
    endpoint: Api.getAdministratorPortfolioLimits,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          page: Number(page),
          perPage: Number(perPage),
          semId,
        },
      }),
      [headers, page, perPage, semId]
    ),
  })

  const breadcrumbsItems = [
    {
      page: `${t('portfolio:portfolio')} - ${t('portfolio:limits')}`,
      path: `/${locale}/administrator/portfolio/limits`,
    },
  ]

  const addButton = (
    <Link to={`/${locale}/administrator/portfolio/limits/add`}>
      <Button variant="blue" className="flex items-center">
        <Plus color="white" /> <span className="ml-2">{t('common:add')}</span>
      </Button>
    </Link>
  )

  const hasData = limits != null && limits.data.length > 0

  return (
    <Section
      title={t('portfolio:limits')}
      breadcrubms={breadcrumbsItems}
      icon={<PortfolioIcon />}
      rightElement={canManagePortfolio ? addButton : undefined}
    >
      <PortfolioSemesterSelect />

      {error != null ? (
        <NoContent
          header={t('error:an_error_occurred')}
          image={Warning}
          subHeader={errorMessage(error as unknown as CommonError)}
        />
      ) : !hasData ? (
        <NoContent
          header={t('error:records_not_found')}
          image={Warning}
          subHeader={t(['ლიმიტები არ მოიძებნა', 'No limits found'])}
        />
      ) : (
        <>
          <PaginationTop pagination={limits.meta.pagination} />
          <Table.Table className="mt-7 w-full">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('student:student')}</Table.Th>
                <Table.Th>{t('person:personal_number')}</Table.Th>
                <Table.Th>{t('portfolio:section')}</Table.Th>
                <Table.Th>{t('common:semester')}</Table.Th>
                <Table.Th>{t('portfolio:limit')}</Table.Th>
                <Table.Th>{t('common:created_at')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {limits.data.map((limit) => (
                <Table.Tr key={limit.id}>
                  <Table.Td>{limit.student?.name}</Table.Td>
                  <Table.Td>{limit.student?.persNum}</Table.Td>
                  <Table.Td>{limit.section?.name}</Table.Td>
                  <Table.Td>{limit.semester}</Table.Td>
                  <Table.Td>{limit.allowed}</Table.Td>
                  <Table.Td className="whitespace-nowrap">{limit.createdAt}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table.Table>
          <PaginationBottom pagination={limits.meta.pagination} />
        </>
      )}
    </Section>
  )
}
